/**
 * TraceExplorer — the FULL-PAGE build-trace explorer (trace-explorer
 * initiative, owner interview 2026-07-24): a React Flow canvas of small
 * process nodes (every LLM call, gate battery, diagnosis, and ASSET is its
 * own box), a right-side inspector drawer with the complete detail for the
 * selected node (deep-linkable via `?node=`), and a synchronized Gantt lane
 * showing real durations + parallelism. One page for every member — the
 * serving callable tiers the content (operators verbatim, members redacted).
 * The compact in-studio panel stays as-is; both render the same trace.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { BuildTraceView } from '../services/types.js';
import { useServices } from '../services/context.js';
import {
  buildExplorerGraph,
  ganttSpans,
  llmSteps,
  type ExplorerNode,
  type GanttSpan,
} from '../studio/traceModel.js';
import { AssetProcessDetail } from '../studio/AssetProcessDetail.js';

const fmtDur = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtCost = (usd: number): string => `$${usd.toFixed(usd >= 0.1 ? 2 : 4)}`;
const fmtOffset = (t: number, start: number): string => `+${((t - start) / 1000).toFixed(1)}s`;

const TONE_COLOR: Record<string, string> = {
  ok: 'var(--ok)',
  bad: 'var(--danger)',
  accent: 'var(--accent)',
  muted: 'var(--muted-fg)',
};

// ── the small process box ─────────────────────────────────────────────────────

type TraceNodeData = { n: ExplorerNode; startedAt: number; selected: boolean };

function TraceNode({ data }: NodeProps<Node<TraceNodeData>>) {
  const { n, startedAt, selected } = data;
  const edge = TONE_COLOR[n.tone ?? 'accent'] ?? TONE_COLOR.accent;
  return (
    <div
      data-tracenode={n.id}
      style={{
        width: n.kind === 'asset' ? 168 : 216,
        border: `2px solid ${selected ? edge : 'var(--border)'}`,
        borderTop: `4px solid ${edge}`,
        borderRadius: 'var(--radius-sm)',
        padding: '5px 8px',
        background: 'var(--bg-panel)',
        boxShadow: selected ? `3px 3px 0 ${edge}` : '2px 2px 0 var(--border)',
        cursor: 'pointer',
        fontSize: 11,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
        <span className="muted" style={{ fontSize: 9, letterSpacing: '.07em', textTransform: 'uppercase' }}>
          {n.eyebrow}
        </span>
        {n.at !== undefined && (
          <span className="muted" style={{ fontSize: 9.5 }}>
            {fmtOffset(n.at, startedAt)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 600, margin: '1px 0' }}>{n.title}</div>
      {n.stat && (
        <div className="muted" style={{ fontSize: 10 }}>
          {n.stat}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { trace: TraceNode };

// ── layout: layered columns, fixed spacing (deterministic per trace) ──────────

function layout(nodes: ExplorerNode[]): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();
  let mainY = 0;
  let assetY = 40;
  for (const n of nodes) {
    if (n.lane === 0) {
      pos.set(n.id, { x: 40, y: mainY });
      mainY += n.kind === 'inputs' ? 92 : 104;
    } else if (n.kind === 'asset-pipeline') {
      pos.set(n.id, { x: 480, y: assetY });
      assetY += 100;
    } else {
      // asset chips: two mini-columns so a 14-asset plan stays compact
      const i = [...pos.keys()].filter((k) => k.startsWith('asset:')).length;
      pos.set(n.id, { x: 440 + (i % 2) * 186, y: assetY + Math.floor(i / 2) * 76 });
    }
  }
  return pos;
}

// ── inspector bodies per node kind ────────────────────────────────────────────

function OpenLog({ gcsUri, label }: { gcsUri: string; label: string }) {
  const services = useServices();
  return (
    <button
      className="btn btn--sm"
      style={{ fontSize: 10.5, marginRight: 6 }}
      onClick={() =>
        services.callable
          .resolveLlmPromptUrl({ gcsUri })
          .then(({ url }) => window.open(url, '_blank', 'noopener'))
          .catch(() => {})
      }
    >
      {label}
    </button>
  );
}

function LlmInspector({ n, redacted }: { n: ExplorerNode; redacted: boolean }) {
  const call = n.steps.find((s) => s.type === 'llm');
  if (!call) return null;
  const sections = Array.isArray(call.sections)
    ? (call.sections as Array<{ name: string; chars: number }>)
    : [];
  return (
    <div style={{ fontSize: 12 }}>
      <p style={{ margin: '0 0 6px' }}>
        {String(call.provider)}/{String(call.model)} · {String(call.inputTokens)}→
        {String(call.outputTokens)} tok ·{' '}
        {typeof call.estimatedCostUsd === 'number' && <>{fmtCost(call.estimatedCostUsd)} ·{' '}</>}
        {typeof call.durationMs === 'number' ? fmtDur(call.durationMs) : '—'} · cap{' '}
        {String(call.maxTokens ?? '—')} · {String(call.stopReason)}
      </p>
      {sections.length > 0 && (
        <div style={{ margin: '6px 0' }}>
          <div className="muted" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            context manifest
          </div>
          <table style={{ fontSize: 11.5, borderSpacing: 0 }}>
            <tbody>
              {sections.map((sec, i) => (
                <tr key={i}>
                  <td style={{ paddingRight: 10 }}>{sec.name}</td>
                  <td className="muted" style={{ textAlign: 'right' }}>
                    {sec.chars.toLocaleString()} chars
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {typeof call.promptPreview === 'string' && (
        <details style={{ margin: '4px 0' }}>
          <summary className="muted" style={{ cursor: 'pointer', fontSize: 11.5 }}>
            prompt preview ({String(call.promptChars)} chars total)
          </summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{call.promptPreview}</pre>
        </details>
      )}
      {typeof call.responsePreview === 'string' && (
        <details style={{ margin: '4px 0' }}>
          <summary className="muted" style={{ cursor: 'pointer', fontSize: 11.5 }}>
            response preview ({String(call.responseChars)} chars total)
          </summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{call.responsePreview}</pre>
        </details>
      )}
      <div style={{ marginTop: 6 }}>
        {typeof call.promptGcsUri === 'string' && call.promptGcsUri && (
          <OpenLog gcsUri={call.promptGcsUri} label="full prompt" />
        )}
        {typeof call.responseGcsUri === 'string' && call.responseGcsUri && (
          <OpenLog gcsUri={call.responseGcsUri} label="full response" />
        )}
      </div>
      {redacted && (
        <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          Prompts, responses, and the context manifest are operator-only.
        </p>
      )}
    </div>
  );
}

function GatesInspector({ n }: { n: ExplorerNode }) {
  return (
    <div style={{ fontSize: 12 }}>
      {n.steps.map((g, i) => (
        <details key={i} style={{ margin: '2px 0' }}>
          <summary style={{ cursor: 'pointer', fontSize: 11.5 }}>
            <span style={{ color: g.ok === true ? 'var(--ok, #30a46c)' : 'var(--danger, #e5484d)' }}>
              {g.ok === true ? '✓' : '✗'}
            </span>{' '}
            {String(g.name)} <span className="muted">({fmtDur(Number(g.durationMs ?? 0))})</span>
          </summary>
          {Array.isArray(g.failedTestNames) && g.failedTestNames.length > 0 && (
            <ul style={{ margin: '2px 0 4px 16px', fontSize: 10.5 }}>
              {(g.failedTestNames as string[]).map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}
          {typeof g.outputDigest === 'string' && g.outputDigest && (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 10.5 }}>{g.outputDigest}</pre>
          )}
        </details>
      ))}
    </div>
  );
}

function RawSteps({ n, startedAt }: { n: ExplorerNode; startedAt: number }) {
  return (
    <div style={{ fontSize: 11.5 }}>
      {n.steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
          <span className="muted" style={{ minWidth: 48, textAlign: 'right' }}>
            {fmtOffset(s.t, startedAt)}
          </span>
          <span style={{ wordBreak: 'break-word' }}>
            <strong>{String(s.type)}</strong>{' '}
            <span className="muted">
              {JSON.stringify({ ...s, t: undefined, type: undefined }).slice(0, 400)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ── the Gantt lane ────────────────────────────────────────────────────────────

function Gantt({
  spans,
  startedAt,
  finishedAt,
  selected,
  onPick,
}: {
  spans: GanttSpan[];
  startedAt: number;
  finishedAt: number;
  selected: string | null;
  onPick: (nodeId: string) => void;
}) {
  const total = Math.max(finishedAt - startedAt, 1);
  const W = 960;
  const ROW = 16;
  const H = spans.length * ROW + 22;
  const x = (t: number): number => ((t - startedAt) / total) * (W - 170) + 160;
  return (
    <div style={{ overflowX: 'auto' }} data-testid="trace-gantt">
      <svg width={W} height={H} style={{ display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={x(startedAt + f * total)} y1={0} x2={x(startedAt + f * total)} y2={H - 16} stroke="var(--border)" strokeDasharray="2 4" />
            <text x={x(startedAt + f * total)} y={H - 4} style={{ fontSize: 9, fill: 'var(--muted-fg)' }} textAnchor="middle">
              +{Math.round((f * total) / 1000)}s
            </text>
          </g>
        ))}
        {spans.map((sp, i) => {
          const y = i * ROW + 2;
          const isSel = selected === sp.nodeId;
          return (
            <g key={sp.nodeId + i} style={{ cursor: 'pointer' }} onClick={() => onPick(sp.nodeId)}>
              <text x={154} y={y + 9} textAnchor="end" style={{ fontSize: 9.5, fill: isSel ? 'var(--fg)' : 'var(--muted-fg)' }}>
                {sp.label.slice(0, 24)}
              </text>
              <rect
                x={x(sp.start)}
                y={y}
                width={Math.max(x(sp.end) - x(sp.start), 2)}
                height={ROW - 5}
                rx={2}
                fill={TONE_COLOR[sp.tone]}
                opacity={isSel ? 1 : 0.55}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── the page ──────────────────────────────────────────────────────────────────

export function TraceExplorer() {
  const { id = '', version = '' } = useParams();
  const v = Number(version);
  const services = useServices();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trace, setTrace] = useState<BuildTraceView | null>(null);
  const [redacted, setRedacted] = useState(false);
  const [error, setError] = useState('');
  const selectedId = searchParams.get('node');

  useEffect(() => {
    let gone = false;
    setTrace(null);
    setError('');
    services.callable
      .getBuildTrace({ creationId: id, version: v })
      .then((res) => {
        if (gone) return;
        setTrace(res.trace);
        setRedacted(res.redacted);
      })
      .catch((e) => !gone && setError((e as Error).message));
    return () => {
      gone = true;
    };
  }, [services, id, v]);

  const graph = useMemo(() => (trace ? buildExplorerGraph(trace) : null), [trace]);
  const spans = useMemo(
    () => (trace && graph ? ganttSpans(graph, trace.startedAt, trace.finishedAt) : []),
    [trace, graph],
  );

  const pick = useCallback(
    (nodeId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nodeId) next.set('node', nodeId);
          else next.delete('node');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const rfNodes: Node<TraceNodeData>[] = useMemo(() => {
    if (!graph || !trace) return [];
    const pos = layout(graph.nodes);
    return graph.nodes.map((n) => ({
      id: n.id,
      type: 'trace',
      position: pos.get(n.id) ?? { x: 0, y: 0 },
      data: { n, startedAt: trace.startedAt, selected: selectedId === n.id },
      draggable: false,
    }));
  }, [graph, trace, selectedId]);

  const rfEdges: Edge[] = useMemo(() => {
    if (!graph) return [];
    return graph.edges.map((e, i) => ({
      id: `e${i}`,
      source: e.from,
      target: e.to,
      label: e.label,
      animated: e.tone === 'bad',
      style: {
        stroke: e.tone === 'bad' ? 'var(--danger)' : 'var(--border)',
        strokeWidth: 1.4,
      },
      labelStyle: { fontSize: 9.5, fill: 'var(--muted-fg)' },
      labelBgStyle: { fill: 'transparent' },
    }));
  }, [graph]);

  const selected = graph?.nodes.find((n) => n.id === selectedId) ?? null;
  const total = trace?.finishedAt ? trace.finishedAt - trace.startedAt : null;
  const spend = trace
    ? llmSteps(trace.steps).reduce(
        (sum, s) => sum + (typeof s.estimatedCostUsd === 'number' ? s.estimatedCostUsd : 0),
        0,
      )
    : 0;

  return (
    <div className="trace-explorer" data-testid="trace-explorer">
      <div className="trace-explorer__header">
        <h2 style={{ fontSize: 16, margin: 0 }}>
          Build trace · v{version}
        </h2>
        {trace && (
          <span className="muted" style={{ fontSize: 12 }}>
            {trace.mode} · attempt {trace.attempt} · {trace.status}
            {total !== null ? ` · ${fmtDur(total)}` : ''}
            {spend > 0 ? ` · ${fmtCost(spend)}` : ''} · {trace.steps.length} steps
            {redacted ? ' · operator detail hidden' : ''}
          </span>
        )}
        <Link to={`/studio/g/${id}?tab=iterate`} className="trace-explorer__back muted">
          ← back to studio
        </Link>
      </div>
      {error && <p className="error-text">{error}</p>}
      {!trace && !error && <p className="muted">Loading trace…</p>}
      {trace && graph && (
        <div className="trace-explorer__content">
          <div className="trace-explorer__main">
            <div className="trace-explorer__canvas">
              <ReactFlow
                nodes={rfNodes}
                edges={rfEdges}
                nodeTypes={nodeTypes}
                onNodeClick={(_, node) => pick(node.id)}
                onPaneClick={() => pick(null)}
                fitView
                minZoom={0.2}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={24} size={1} />
                <MiniMap
                  pannable
                  zoomable
                  style={{ width: 120, height: 84 }}
                  bgColor="var(--bg-panel-2)"
                  maskColor="color-mix(in srgb, var(--accent) 14%, transparent)"
                  nodeColor="var(--accent)"
                />
                <Controls showInteractive={false} />
              </ReactFlow>
            </div>
            <Gantt
              spans={spans}
              startedAt={trace.startedAt}
              finishedAt={trace.finishedAt ?? trace.startedAt + 1}
              selected={selectedId}
              onPick={pick}
            />
          </div>
          {selected && (
            <aside
              className="trace-explorer__inspector"
              data-testid="trace-inspector"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div className="muted" style={{ fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase' }}>
                    {selected.eyebrow}
                  </div>
                  <h3 style={{ fontSize: 14, margin: '1px 0 8px' }}>{selected.title}</h3>
                </div>
                <button className="btn btn--sm" style={{ fontSize: 11 }} onClick={() => pick(null)}>
                  ✕
                </button>
              </div>
              {(selected.kind === 'llm' || selected.kind === 'quality') && (
                <LlmInspector n={selected} redacted={redacted} />
              )}
              {selected.kind === 'gates' && <GatesInspector n={selected} />}
              {selected.kind === 'asset' && (
                <AssetProcessDetail chain={selected.steps} startedAt={trace.startedAt} redacted={redacted} />
              )}
              {selected.kind !== 'llm' &&
                selected.kind !== 'quality' &&
                selected.kind !== 'gates' &&
                selected.kind !== 'asset' && <RawSteps n={selected} startedAt={trace.startedAt} />}
              {(selected.kind === 'quality' || selected.kind === 'gates') && (
                <details style={{ marginTop: 8 }}>
                  <summary className="muted" style={{ cursor: 'pointer', fontSize: 11.5 }}>
                    raw steps ({selected.steps.length})
                  </summary>
                  <RawSteps n={selected} startedAt={trace.startedAt} />
                </details>
              )}
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
